<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { timezoneOptions } from 'dashboard/helper/timezoneOptions';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Ordem de exibição: segunda→domingo. Chave = wday do Ruby (domingo=0),
// mesma convenção do Agno::ScheduleGate.
const DAY_ORDER = ['1', '2', '3', '4', '5', '6', '0'];
const DAY_LABELS = {
  0: 'SUN',
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
};

const timezoneSelectOptions = computed(() =>
  timezoneOptions({ current: props.modelValue?.timezone })
);

const config = computed(() => ({
  enabled: false,
  timezone: 'America/Sao_Paulo',
  days: {},
  ...props.modelValue,
}));

const update = patch =>
  emit('update:modelValue', { ...config.value, ...patch });

const dayConfig = wday => ({
  enabled: false,
  start: '08:00',
  end: '18:00',
  ...(config.value.days?.[wday] || {}),
});

const updateDay = (wday, patch) =>
  update({
    days: { ...config.value.days, [wday]: { ...dayConfig(wday), ...patch } },
  });

// Fim menor que o início = janela que atravessa a meia-noite (ex.: 22h→06h).
const isOvernight = wday => {
  const day = dayConfig(wday);
  return day.enabled && day.start !== day.end && day.end < day.start;
};
const isAllDay = wday => {
  const day = dayConfig(wday);
  return day.enabled && day.start === day.end;
};

// Resumo em palavras do que foi configurado, agrupando dias consecutivos com
// a mesma janela: "Seg a Sex 17:01 até 07:59 (termina no dia seguinte)".
// É o que garante que a pessoa entenda como a IA vai atender.
const summary = computed(() => {
  const groups = [];
  DAY_ORDER.forEach(wday => {
    const day = dayConfig(wday);
    if (!day.enabled) return;
    const window = isAllDay(wday)
      ? t('AI_AGENTS.SCHEDULE.ALL_DAY')
      : `${day.start} ${t('AI_AGENTS.SCHEDULE.UNTIL')} ${day.end}${
          isOvernight(wday) ? ` (${t('AI_AGENTS.SCHEDULE.NEXT_DAY')})` : ''
        }`;
    const label = t(`AI_AGENTS.RULES.SCHEDULE_FIELDS.DAYS.${DAY_LABELS[wday]}`);
    const last = groups[groups.length - 1];
    const prevIndex = DAY_ORDER.indexOf(wday) - 1;
    const contiguous =
      last && last.window === window && last.lastIndex === prevIndex;
    if (contiguous) {
      last.to = label;
      last.lastIndex = prevIndex + 1;
    } else {
      groups.push({ from: label, to: label, window, lastIndex: prevIndex + 1 });
    }
  });
  return groups.map(g =>
    g.from === g.to
      ? `${g.from}: ${g.window}`
      : `${g.from} a ${g.to}: ${g.window}`
  );
});
</script>

<template>
  <div class="space-y-5">
    <div class="flex items-center justify-between p-4 bg-n-alpha-1 rounded-xl">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.SCHEDULE.ENABLED') }}
        </h4>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('AI_AGENTS.SCHEDULE.ENABLED_DESCRIPTION') }}
        </p>
      </div>
      <Switch
        :model-value="config.enabled"
        @update:model-value="update({ enabled: $event })"
      />
    </div>

    <template v-if="config.enabled">
      <div class="max-w-72">
        <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
          {{ t('AI_AGENTS.SCHEDULE.TIMEZONE') }}
        </label>
        <ComboBox
          :model-value="config.timezone"
          :options="timezoneSelectOptions"
          :search-placeholder="t('AI_AGENTS.SCHEDULE.TIMEZONE_SEARCH')"
          @update:model-value="update({ timezone: $event })"
        />
      </div>

      <!-- Uma linha por dia (dia + início "até" fim), sem quebra, com o selo
           de virada de noite embaixo. Cabe no painel lateral (384px). -->
      <div class="space-y-1.5">
        <div
          v-for="wday in DAY_ORDER"
          :key="wday"
          class="flex flex-col gap-1 rounded-xl bg-n-alpha-1 px-2.5 py-2"
        >
          <div class="flex items-center gap-2">
            <Switch
              :model-value="dayConfig(wday).enabled"
              @update:model-value="updateDay(wday, { enabled: $event })"
            />
            <span
              v-tooltip="t(`AI_AGENTS.SCHEDULE.DAYS.${DAY_LABELS[wday]}`)"
              class="w-8 shrink-0 text-xs font-medium text-n-slate-12"
            >
              {{
                t(`AI_AGENTS.RULES.SCHEDULE_FIELDS.DAYS.${DAY_LABELS[wday]}`)
              }}
            </span>
            <template v-if="dayConfig(wday).enabled">
              <input
                type="time"
                :value="dayConfig(wday).start"
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-1.5 py-1 text-xs text-n-slate-12"
                @change="updateDay(wday, { start: $event.target.value })"
              />
              <span class="shrink-0 text-[11px] text-n-slate-11">
                {{ t('AI_AGENTS.SCHEDULE.UNTIL') }}
              </span>
              <input
                type="time"
                :value="dayConfig(wday).end"
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-1.5 py-1 text-xs text-n-slate-12"
                @change="updateDay(wday, { end: $event.target.value })"
              />
            </template>
            <span v-else class="text-[11px] text-n-slate-10">
              {{ t('AI_AGENTS.SCHEDULE.DAY_OFF') }}
            </span>
          </div>
          <span
            v-if="isOvernight(wday)"
            class="ml-10 self-start rounded-md bg-n-amber-3 px-1.5 py-0.5 text-[10px] font-medium text-n-amber-11"
          >
            {{ t('AI_AGENTS.SCHEDULE.NEXT_DAY') }}
          </span>
          <span
            v-else-if="isAllDay(wday)"
            class="ml-10 self-start rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-medium text-n-slate-11"
          >
            {{ t('AI_AGENTS.SCHEDULE.ALL_DAY') }}
          </span>
        </div>
      </div>

      <!-- Resumo em palavras: tira a dúvida de "assim ele entende?" -->
      <div
        v-if="summary.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5"
      >
        <p class="mb-1 text-[11px] font-semibold uppercase text-n-slate-10">
          {{ t('AI_AGENTS.SCHEDULE.SUMMARY_TITLE') }}
        </p>
        <ul class="space-y-0.5">
          <li
            v-for="line in summary"
            :key="line"
            class="text-xs text-n-slate-12"
          >
            {{ line }}
          </li>
        </ul>
      </div>

      <p class="text-xs text-n-slate-11">
        {{ t('AI_AGENTS.SCHEDULE.HINT') }}
      </p>
    </template>
  </div>
</template>
